import { useRef, useState } from "react";
import api, { resolveImage, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable image input for the admin CMS.
 * Supports both URL entry and device upload via /api/admin/upload.
 *
 * Props:
 *   value: current URL string
 *   onChange: (newUrl: string) => void
 *   label?: string  — shown above the input
 *   testId?: string — appended to buttons/inputs
 *   compact?: bool  — smaller preview (for grids)
 */
export default function ImageInput({ value, onChange, label, testId = "img", compact = false }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const previewSize = compact ? "w-14 h-14" : "w-20 h-20";

  return (
    <div>
      {label && (
        <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">{label}</label>
      )}
      <div className="flex gap-2 mt-1 items-start">
        <div className={`${previewSize} shrink-0 border border-slate-200 bg-slate-50 overflow-hidden`}>
          {value && <img src={resolveImage(value)} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <Input
              data-testid={`${testId}-url`}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Paste image URL or upload →"
              className="rounded-none border-slate-300 flex-1"
            />
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="px-2 border border-slate-300 hover:bg-slate-100"
                aria-label="Clear image"
                data-testid={`${testId}-clear`}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={doUpload} />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid={`${testId}-upload`}
            className="rounded-none uppercase text-xs font-bold tracking-widest w-full"
          >
            <Upload size={14} className="mr-1" /> {busy ? "Uploading…" : "Upload from device"}
          </Button>
        </div>
      </div>
    </div>
  );
}
